import { useGetList, useGetOne, type Identifier } from "ra-core";
import { useMemo } from "react";

import {
  commissionsByOrder,
  type Commission,
} from "../commissions/commissions";
import type { GulaOrder, GulaPayment, GulaSubscription } from "../gulaTypes";
import {
  computeProfit,
  defaultCostSheet,
  recurringActuals,
  sheetFromRecord,
  type CostSettings,
  type CostSheet,
  type OrderCostsRecord,
  type ProfitResult,
  type RecurringActuals,
} from "./profit";

export type ProfitRow = {
  order: GulaOrder;
  record?: OrderCostsRecord;
  sheet: CostSheet;
  /** What the sheet would be with the usual costs */
  defaults: CostSheet;
  actuals: RecurringActuals;
  result: ProfitResult;
  /** Maintenance and rental are counted on the client's latest sale only */
  carriesRecurring: boolean;
};

const NO_RECURRING: RecurringActuals = {
  collectedGross: 0,
  paymentsCount: 0,
  monthsActive: 0,
};

const groupByContact = <T extends { contact_id: Identifier }>(items: T[]) => {
  const groups = new Map<string, T[]>();
  items.forEach((item) => {
    const key = String(item.contact_id);
    groups.set(key, [...(groups.get(key) ?? []), item]);
  });
  return groups;
};

/** Stripe payments and subscriptions synced into the CRM. */
export const useStripeData = () => {
  const payments = useGetList<GulaPayment>("gula_payments", {
    pagination: { page: 1, perPage: 5000 },
    sort: { field: "paid_at", order: "DESC" },
  });
  const subscriptions = useGetList<GulaSubscription>("gula_subscriptions", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "id", order: "ASC" },
  });
  return {
    payments: payments.data ?? [],
    subscriptions: subscriptions.data ?? [],
    isPending: payments.isPending || subscriptions.isPending,
  };
};

export const useCostSettings = () =>
  useGetOne<CostSettings>("gula_cost_settings", { id: 1 });

/** Every sale with its cost sheet (saved or estimated) and its profit. */
export const useProfitRows = () => {
  const { payments, subscriptions, isPending: stripePending } = useStripeData();
  const { data: settings, isPending: settingsPending } = useCostSettings();
  const { data: orders = [], isPending: ordersPending } = useGetList<GulaOrder>(
    "gula_orders_summary",
    {
      filter: { "stage@neq": "cancelled" },
      pagination: { page: 1, perPage: 2000 },
      sort: { field: "created_at", order: "DESC" },
    },
  );
  const { data: records = [], isPending: costsPending } =
    useGetList<OrderCostsRecord>("gula_order_costs", {
      pagination: { page: 1, perPage: 2000 },
      sort: { field: "id", order: "ASC" },
    });
  const { data: commissions = [] } = useGetList<Commission>(
    "gula_commissions",
    {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "id", order: "ASC" },
    },
  );
  const rows = useMemo<ProfitRow[]>(() => {
    if (!settings) return [];
    const now = new Date();
    const paymentsByContact = groupByContact(payments);
    const subscriptionsByContact = groupByContact(subscriptions);
    const recordByOrder = new Map(
      records.map((record) => [String(record.order_id), record]),
    );
    const commissionByOrder = commissionsByOrder(commissions);
    const latestOrderOfContact = new Map<string, Identifier>();
    // orders come newest first
    orders.forEach((order) => {
      const key = String(order.contact_id);
      if (!latestOrderOfContact.has(key)) {
        latestOrderOfContact.set(key, order.id);
      }
    });

    return orders.map((order) => {
      const contactKey = String(order.contact_id);
      const carriesRecurring =
        String(latestOrderOfContact.get(contactKey)) === String(order.id);
      const record = recordByOrder.get(String(order.id));
      const draft = defaultCostSheet({
        order,
        settings,
        subscriptions: carriesRecurring
          ? (subscriptionsByContact.get(contactKey) ?? [])
          : [],
      });
      const sheet = record
        ? sheetFromRecord(record, order, draft.shipping_cost)
        : draft;
      const actuals = carriesRecurring
        ? recurringActuals(paymentsByContact.get(contactKey) ?? [], now)
        : NO_RECURRING;
      return {
        order,
        record,
        sheet,
        defaults: draft,
        actuals,
        result: computeProfit(
          sheet,
          actuals,
          commissionByOrder.get(String(order.id)) ?? 0,
        ),
        carriesRecurring,
      };
    });
  }, [orders, records, settings, payments, subscriptions, commissions]);

  return {
    rows,
    settings,
    payments,
    subscriptions,
    isPending:
      stripePending || settingsPending || ordersPending || costsPending,
  };
};
